import { useMemo, useState } from 'react';
import { Stack } from '@chakra-ui/react';
import Wizard from 'components/Wizard/Wizard';
import ArtistDataSuggestionForm, { ArtistDataSuggestionFormData } from 'components/Form/ArtistDataSuggestionForm/ArtistDataSuggestionForm';
import ArtistPreview from 'components/Artist/ArtistPreview/ArtistPreview';
import { ArtistModel } from 'models';

interface ArtistCreationWizardProps {
  onSubmit?: (data: ArtistDataSuggestionFormData) => void;
  isLoading?: boolean;
}

export default function ArtistCreationWizard({ onSubmit, isLoading = false }: ArtistCreationWizardProps) {
  const [formData, setFormData] = useState<ArtistDataSuggestionFormData | null>(null);

  const previewArtist = useMemo(() => {
    if (!formData) return null;

    return new ArtistModel({
      id: '',
      name: formData.name || '',
      description: formData.description || '',
      image: formData.image || '',
      aliases: [],
      birth_year: formData.birthYear,
      country: formData.country || undefined,
      contact_info: {
        instagram_link: formData.instagram || '',
        artsy_link: formData.artsy || '',
        representative_gallery: formData.representativeGallery || '',
        website: formData.website || '',
      }
    });
  }, [formData]);

  const handleSubmit = () => {
    if (formData) {
      onSubmit?.(formData);
    }
  };

  const isFormValid = Boolean(
    formData?.name &&
    formData?.birthYear &&
    formData?.description &&
    formData?.country?.code &&
    formData?.image
  );

  const wizardSteps = [
    {
      label: 'Artist Details',
      content: (
        <ArtistDataSuggestionForm
          onChange={setFormData}
          highlightAllFields
          submitLoading={false}
          showSubmitButton={false}
          requiredOptions={['name', 'birthYear', 'description', 'country', 'image']}
          displayOptions={['name', 'birthYear', 'description', 'country', 'image', 'artsy', 'instagram', 'website', 'representativeGallery']}
        />
      ),
      requiresValidation: true,
      isValidated: isFormValid,
    },
    {
      label: 'Confirm Details',
      content: (
        <Stack spacing={6} align="center">
          {previewArtist && <ArtistPreview artist={previewArtist} />}
        </Stack>
      )
    }
  ];

  return (
    <Wizard
      steps={wizardSteps}
      onSubmit={handleSubmit}
      loading={isLoading}
      showButtons={true}
      showStepper={true}
    />
  );
}
